import { EventEmitter } from 'events';
import { io } from 'socket.io-client';
import waitFor from 'dt-common/isomorphic-helpers/waitFor';
import Colors from '~/constants/Colors';
import { ApplicationDispatcher } from '~/flux/dispatchers';
import { registerDispatchHandlers } from '~/Tools';
import Config from '~/constants/Config';
import text from '~/text';

if (Config.PLATFORM === 'yandex') {
  let _ysdk_ready_sent = false;

  (function (d) {
    var s = d.createElement('script');
    s.src = 'https://sdk.games.s3.yandex.net/sdk.js';
    s.async = true;
    s.onload = initYandexSDK;
    d.body.append(s);
  })(document);

  function initYandexSDK() {
    window.YaGames.init().then((ysdk) => {
      console.info('Yandex SDK initialized');
      window.ysdk = ysdk;
      !_ysdk_ready_sent && ysdk?.features?.LoadingAPI?.ready();
      _ysdk_ready_sent = true;

      // add start/stop analytics on visibility change
      window.addEventListener('visibilitychange', () => {
        if (document.hidden || document.webkitHidden) {
          ysdk?.features?.GameplayAPI?.stop();
        } else {
          ysdk?.features?.GameplayAPI?.start();
        }
      });

      ApplicationStore.emit(ApplicationStore.YANDEX_SDK_READY);
    });
  }
} else if (Config.PLATFORM === 'crazygames') {
  async function initCrazyGamesSDK() {
    await waitFor(() => !!window.CrazyGames);
    await CrazyGames.SDK.init();
    CrazyGames.SDK.is_dt_initialized = true;

    window.addEventListener('visibilitychange', () => {
      if (document.hidden || document.webkitHidden) {
        CrazyGames.SDK.game.gameplayStop();
      } else {
        CrazyGames.SDK.game.gameplayStart();
      }
    });
  }

  (function (d) {
    var s = d.createElement('script');
    s.src = 'https://sdk.crazygames.com/crazygames-sdk-v3.js';
    s.async = true;
    s.onload = initCrazyGamesSDK;
    d.body.append(s);
  })(document);
} else if (Config.PLATFORM === 'kongregate') {
  const a = document.createElement('script');
  a.type = 'text/javascript';
  a.async = true;
  a.src = 'https://cdn1.kongregate.com/javascripts/kongregate_api.js';
  const b = document.getElementsByTagName('script')[0];
  b.parentNode.insertBefore(a, b);
}

// the stuff we serve:
let socket = null;
let assets_loaded = false;
let playersOnline = 0;
let window_has_focus = true;

// on CrazyGames, track game loading time
if (Config.PLATFORM === 'crazygames') {
  (async () => {
    await waitFor(() => window?.CrazyGames?.SDK?.is_dt_initialized);
    !assets_loaded && window?.CrazyGames?.SDK?.game?.loadingStart();
  })();
}

const ApplicationStore = Object.assign({}, EventEmitter.prototype, {
  API_ERROR: 'API_ERROR',
  ASSETS_LOADED: 'ASSETS_LOADED',
  CONNECT_ERROR_INVALID_TOKEN: 'CONNECT_ERROR_INVALID_TOKEN',
  GAMESPARKS_ERROR: 'GAMESPARKS_ERROR',
  NUM_PLAYERS_ONLINE: 'NUM_PLAYERS_ONLINE',
  SOCKET_CONNECTED: 'SOCKET_CONNECTED',
  SOCKET_DISCONNECTED: 'SOCKET_DISCONNECTED',
  WINDOW_LOST_FOCUS: 'WINDOW_LOST_FOCUS',
  WINDOW_GOT_FOCUS: 'WINDOW_GOT_FOCUS',
  YANDEX_SDK_READY: 'YANDEX_SDK_READY',

  getAll() {
    return {
      socket,
      assets_loaded,
      playersOnline,
      window_has_focus,
    };
  },
});
export default ApplicationStore;

ApplicationStore.setMaxListeners(15);

ApplicationDispatcher.register(
  registerDispatchHandlers({
    [ApplicationDispatcher.ASSETS_LOADED]: onAssetsLoaded,
    [ApplicationDispatcher.CONNECT_API_SOCKET]: connectApiSocket,
    [ApplicationDispatcher.NO_REDLOCK]: onNoRedlock,
  })
);

function connectApiSocket({
  browser_id,
  crazygames_token,
  firebase_token,
  kong_token,
  kong_userId,
  steam_accountId,
  steamId64,
  steam_game_language,
  yandex_accountId,
  is_token_refresh,
}) {
  try {
    socket?.close();
    socket = io(
      Config.DEMO_MODE
        ? 'https://site--dt-api-staging--m2cp7fdx2bb9.code.run'
        : process.env.DT_API_ADDRESS || 'http://localhost:3001',
      {
        auth: {
          browser_id,
          crazygames_token,
          firebase_token,
          kong_token,
          kong_userId,
          steam_accountId,
          steamId64: steamId64?.toString(),
          steam_game_language,
          yandex_accountId,
        },
        extraHeaders: {
          'X-DeviceID': browser_id,
        },
        secure: true,
        timeout: 3000,
      }
    );

    socket.on('connected', onSocketConnected);
    socket.on('connect_error', (err) => {
      console.error(`connect_error due to ${err.message}`);
    });
    socket.io.on('reconnect_error', (err) => {
      console.error(`reconnect_error due to ${err.message}`);
    });
    socket.io.on('reconnect_failed', () => {
      console.error('reconnect_failed');
    });
    socket.on('disconnect', (reason) => {
      console.warn('socket disconnect. reason: ', reason);
      if (reason === 'io server disconnect') {
        // the disconnection was initiated by the server, you need to reconnect manually
        socket.connect();
      }
      // else the socket will automatically try to reconnect
    });
    socket.on('error', onSocketError);
    socket.on('api_server_error', onApiServerError);
    socket.on('noRedlock', onNoRedlock);
    socket.on('numPlayersOnline', onNumPlayersOnline);
    socket.on('battleEngineError', onBattleEngineError);

    if (is_token_refresh) {
      socket.do_reconnect_player = true;
    }
  } catch (err) {
    logError(err, {
      module: 'ApplicationStore',
      func: 'connectApiSocket',
    });
  }
}

function onSocketError(err) {
  logError(err);
}

function onApiServerError(data) {
  const { requestName, reqData, message, stack } = data;

  // log error data to the console
  console.group('************************\nAPI Server Error');
  console.error(stack);
  console.error('requestName', requestName);
  console.error('reqData', reqData);
  console.groupEnd();

  // surface to the player
  const translated_message = text(`server.error.${message}`);
  $addMessageLogMessage(translated_message || text('ui.server_error'), Colors.RED);

  ApplicationStore.emit(ApplicationStore.API_ERROR, data);
}

function onNoRedlock(lock_name) {
  console.warn('no redlock for request:', lock_name);
  ApplicationStore.emit(ApplicationStore.API_ERROR, {
    message: `no redlock for lock_name: ${lock_name}`,
  });
}

async function onAssetsLoaded() {
  assets_loaded = true;
  ApplicationStore.emit(ApplicationStore.ASSETS_LOADED);

  if (Config.PLATFORM === 'crazygames') {
    await waitFor(() => window?.CrazyGames?.SDK?.is_dt_initialized);
    window?.CrazyGames?.SDK?.game?.loadingStop();
  }
}

function onSocketConnected() {
  console.info('socket connected');
  ApplicationStore.emit(ApplicationStore.SOCKET_CONNECTED, socket);
}

function onNumPlayersOnline(data) {
  playersOnline = data.num;
  ApplicationStore.emit(ApplicationStore.NUM_PLAYERS_ONLINE, playersOnline);
}

function onBattleEngineError(err) {
  logError(err, {
    module: 'ApplicationStore',
    func: 'onBattleEngineError',
  });
}

window.addEventListener('focus', onWindowActive);
window.addEventListener('blur', onWindowInactive);
function onWindowInactive() {
  window_has_focus = false;
  ApplicationStore.emit(ApplicationStore.WINDOW_LOST_FOCUS);
}
function onWindowActive() {
  window_has_focus = true;
  ApplicationStore.emit(ApplicationStore.WINDOW_GOT_FOCUS);
}
